/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { RootState } from '../configureStore';
import { AccountInfo, UserAccount, User, UserRef } from '../../data/types';
import { chatStreamCompleteAction } from './aiAgentReducer';
import { signOut } from './authReducer';

const inputCreditRate = 500 / 1000000;
const outputCreditRate = 3000 / 1000000;

export type AccountState = {
    portalAccount?: AccountInfo;
    userAccounts: UserAccount[];
    primaryAccount?: UserAccount;
    getUserRef?: (username: string) => UserRef | undefined;
};

export const useAccountState = () => {
    const acctState = useSelector((state: RootState | any) => state.accounts as AccountState);
    const getUserRef = (username?: string): UserRef | undefined => {
        return acctState.primaryAccount?.accountUsers.find((x) => x.username == username);
    };
    const getUserName = (userId: string): string => {
        return acctState.primaryAccount?.accountUsers.find(
            (user) => user.userId === userId || user.username === userId,
        )?.name || '';
    };
    return {
        ...acctState,
        getUserRef,
        getUserName,
    };
};

// Define the initial state using that type
const initialState: AccountState = {
    userAccounts: [],
    primaryAccount: undefined,
};

export const accountSlice = createSlice({
    name: 'accounts',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState: initialState,
    reducers: {
        setPortalAccountAction: (state, action: PayloadAction<AccountInfo>) => {
            state.portalAccount = action.payload;
            state.userAccounts = [];
            state.primaryAccount = undefined;
        },
        setAccountsAction: (state, action: PayloadAction<UserAccount[]>) => {
            return {
                ...state,
                userAccounts: action.payload,
                portalAccount: action.payload.length ? state.portalAccount : undefined,
            };
        },
        addAccount: (state, action: PayloadAction<UserAccount>) => {
            return {
                ...state,
                userAccounts: [...state.userAccounts, action.payload],
            };
        },
        setPrimaryAccountAction: (state, action: PayloadAction<UserAccount | undefined>) => {
            return {
                ...state,
                primaryAccount: action.payload,
            };
        },
        acceptTOSAction: (state) => {
            const newUser = { ...state.primaryAccount?.user, acceptedTOS: new Date() } as User;
            const newAcct = { ...state.primaryAccount, user: newUser } as UserAccount;

            return {
                ...state,
                primaryAccount: newAcct,
            };
        },
        replaceUserInAccountAction: (state, action: PayloadAction<User>) => {
            const modUser = action.payload;
            if (!modUser.accountId) return state;

            const replaceUserInAcct = (account: UserAccount) => {
                let newAcct = { ...account };
                if (account.user.username == modUser.username) {
                    newAcct = { ...account, user: modUser };
                }

                const userIndex = account.accountUsers.findIndex((acctUser) => acctUser.username === modUser.username);
                if (userIndex !== -1) {
                    newAcct = {
                        ...newAcct,
                        accountUsers: [
                            ...account.accountUsers.slice(0, userIndex),
                            modUser as UserRef,
                            ...account.accountUsers.slice(userIndex + 1),
                        ],
                    };
                } else {
                    newAcct = { ...newAcct, accountUsers: [...account.accountUsers, modUser as UserRef] };
                }

                return newAcct;
            };

            const accountId = action.payload.accountId;
            let primAcct: UserAccount = state.primaryAccount!;

            if (state.primaryAccount?.account.accountId == accountId) {
                primAcct = replaceUserInAcct(primAcct);
            }

            let userAccts = [];
            const acctIndex = state.userAccounts.findIndex((acct) => acct.account.accountId === accountId);
            if (acctIndex !== -1) {
                const updatedAcct = replaceUserInAcct(state.userAccounts[acctIndex]);
                userAccts = [
                    ...state.userAccounts.slice(0, acctIndex),
                    updatedAcct,
                    ...state.userAccounts.slice(acctIndex + 1),
                ];
            } else {
                userAccts = [...state.userAccounts];
            }

            return {
                ...state,
                primaryAccount: primAcct,
                userAccounts: userAccts,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(signOut, () => initialState);
        builder.addCase(chatStreamCompleteAction, (state, action) => {
            const creditsUsed =
                action.payload.usage.promptTokens * inputCreditRate +
                action.payload.usage.completionTokens * outputCreditRate;

            if (!state.primaryAccount?.account.accountId) return;
            
            const accountId = state.primaryAccount?.account.accountId;

            state.primaryAccount.usedAICredits += creditsUsed;

            const account = state.userAccounts.find((item) => item.account.accountId === accountId);
            if (account && account !== state.primaryAccount) {
                account.usedAICredits += creditsUsed;
            }
        });
    },
});

export const { setPortalAccountAction, setAccountsAction, setPrimaryAccountAction, replaceUserInAccountAction, acceptTOSAction } =
    accountSlice.actions;

export default accountSlice.reducer;
